import { NormalizedExpression } from '../NormalizedExpression';
import type { AttributeCriterion, InnerRule, Value } from './types';

type Criterion = {
  attribute?: AttributeCriterion;
  segment?: {
    segment: string;
  };
};

type TargetingExpression = {
  ref?: string;
  not?: TargetingExpression;
  and?: {
    operands?: TargetingExpression[];
  };
  or?: {
    operands?: TargetingExpression[];
  };
};

type SegmentTargeting = {
  expression?: TargetingExpression;
  criteria?: {
    [key: string]: Criterion;
  };
};

type NormalizedValue = NormalizedExpression.BinaryExpression['value'];

const convertValue = (value: Value): NormalizedValue => {
  if (value.stringValue !== undefined) {
    return value.stringValue;
  }
  if (value.numberValue !== undefined) {
    return value.numberValue;
  }
  if (value.boolValue !== undefined) {
    return value.boolValue;
  }
  if (value.timestampValue !== undefined) {
    return value.timestampValue;
  }
  if (value.versionValue !== undefined) {
    return value.versionValue;
  }
  return null;
};

const convertValues = (values: Value[]): NormalizedValue[] => {
  return values.map(convertValue);
};

export const isNullValue = (value: Value): boolean => {
  return Object.keys(value).length === 0;
};

export const valueToString = (value: Value): string => {
  return NormalizedExpression.valueToString(convertValue(value));
};

const normalizeInnerRule = (
  rule: InnerRule,
  isNegated: boolean = false,
): NormalizedExpression.InnerExpression | null => {
  // Handle eqRule (equals/not equals)
  if (rule.eqRule) {
    const value = rule.eqRule.value;

    // Check if this is a null check - but inner rules don't support unary operators
    // So we'll treat null as a regular value
    if (isNullValue(value)) {
      // For inner rules, null values are treated as regular binary comparisons
      const operator: NormalizedExpression.BinaryOperator = isNegated
        ? '!='
        : '=';
      return {
        type: 'binary',
        operator,
        value: convertValue(value),
      };
    }

    // Regular equality check (binary operator)
    const operator: NormalizedExpression.BinaryOperator = isNegated
      ? '!='
      : '=';
    return {
      type: 'binary',
      operator,
      value: convertValue(value),
    };
  }

  // Handle setRule (any-of/none-of)
  if (rule.setRule) {
    const operator: NormalizedExpression.SetOperator = isNegated
      ? 'none-of'
      : 'any-of';
    return {
      type: 'set',
      operator,
      values: convertValues(rule.setRule.values),
    };
  }

  // Handle rangeRule
  if (rule.rangeRule) {
    const { rangeRule } = rule;
    const start = rangeRule.startInclusive ?? rangeRule.startExclusive;
    const end = rangeRule.endInclusive ?? rangeRule.endExclusive;

    if (!start || !end) {
      // Handle open-ended ranges as binary comparisons
      if (start) {
        let operator: NormalizedExpression.BinaryOperator;
        if (rangeRule.startInclusive) {
          operator = isNegated ? '<' : '>=';
        } else {
          operator = isNegated ? '<=' : '>';
        }
        return {
          type: 'binary',
          operator,
          value: convertValue(start),
        };
      }
      if (end) {
        let operator: NormalizedExpression.BinaryOperator;
        if (rangeRule.endInclusive) {
          operator = isNegated ? '>' : '<=';
        } else {
          operator = isNegated ? '>=' : '<';
        }
        return {
          type: 'binary',
          operator,
          value: convertValue(end),
        };
      }
      return null;
    }

    // Closed range
    const range: NormalizedExpression.RangeSpec = {
      start: convertValue(start),
      end: convertValue(end),
      startInclusive: !!rangeRule.startInclusive,
      endInclusive: !!rangeRule.endInclusive,
    };

    return {
      type: 'range',
      operator: isNegated ? 'not-between' : 'between',
      range,
    };
  }

  return null;
};

export const normalizeAttributeCriterion = (
  criterion: AttributeCriterion,
  isNegated: boolean = false,
): NormalizedExpression.Expression | null => {
  const { attributeName } = criterion;

  // Handle eqRule (equals/not equals)
  if (criterion.eqRule) {
    const value = criterion.eqRule.value;

    // Check if this is a null check (unary operator)
    if (isNullValue(value)) {
      const operator: NormalizedExpression.UnaryOperator = isNegated
        ? 'is-not-null'
        : 'is-null';
      return {
        type: 'unary',
        attributeName,
        operator,
      };
    }

    // Regular equality check (binary operator)
    const operator: NormalizedExpression.BinaryOperator = isNegated
      ? '!='
      : '=';
    return {
      type: 'binary',
      attributeName,
      operator,
      value: convertValue(value),
    };
  }

  // Handle setRule (any-of/none-of)
  if (criterion.setRule) {
    const operator: NormalizedExpression.SetOperator = isNegated
      ? 'none-of'
      : 'any-of';
    return {
      type: 'set',
      attributeName,
      operator,
      values: convertValues(criterion.setRule.values),
    };
  }

  // Handle rangeRule
  if (criterion.rangeRule) {
    const { rangeRule } = criterion;
    const start = rangeRule.startInclusive ?? rangeRule.startExclusive;
    const end = rangeRule.endInclusive ?? rangeRule.endExclusive;

    if (!start || !end) {
      // Handle open-ended ranges as binary comparisons
      if (start) {
        let operator: NormalizedExpression.BinaryOperator;
        if (rangeRule.startInclusive) {
          operator = isNegated ? '<' : '>=';
        } else {
          operator = isNegated ? '<=' : '>';
        }
        return {
          type: 'binary',
          attributeName,
          operator,
          value: convertValue(start),
        };
      }
      if (end) {
        let operator: NormalizedExpression.BinaryOperator;
        if (rangeRule.endInclusive) {
          operator = isNegated ? '>' : '<=';
        } else {
          operator = isNegated ? '>=' : '<';
        }
        return {
          type: 'binary',
          attributeName,
          operator,
          value: convertValue(end),
        };
      }
      return null;
    }

    // Closed range
    const range: NormalizedExpression.RangeSpec = {
      start: convertValue(start),
      end: convertValue(end),
      startInclusive: !!rangeRule.startInclusive,
      endInclusive: !!rangeRule.endInclusive,
    };

    return {
      type: 'range',
      attributeName,
      operator: isNegated ? 'not-between' : 'between',
      range,
    };
  }

  // Handle anyRule (nested expression with ANY semantics)
  if (criterion.anyRule?.rule) {
    const rule = criterion.anyRule.rule;
    const innerExpression = normalizeInnerRule(rule, isNegated);
    if (!innerExpression) {
      return null;
    }
    return {
      type: 'array',
      attributeName,
      operator: 'match-any',
      rule: innerExpression,
    };
  }

  // Handle allRule (nested expression with ALL semantics)
  if (criterion.allRule?.rule) {
    const rule = criterion.allRule.rule;
    const innerExpression = normalizeInnerRule(rule, isNegated);
    if (!innerExpression) {
      return null;
    }
    return {
      type: 'array',
      attributeName,
      operator: 'match-all',
      rule: innerExpression,
    };
  }

  return null;
};

export const normalizeSegmentCriterion = (
  criterion: { segment: string },
  isNegated: boolean = false,
): NormalizedExpression.SegmentExpression => {
  const operator: NormalizedExpression.SegmentOperator = isNegated
    ? 'not-in-segment'
    : 'in-segment';
  return {
    type: 'segment',
    operator,
    segmentId: criterion.segment,
  };
};

export const normalizeCriteria = (
  criteria: { [key: string]: Criterion } | null | undefined,
): NormalizedExpression.Expression[] => {
  if (!criteria) {
    return [];
  }

  return Object.values(criteria)
    .map(criterion => {
      if (criterion?.attribute) {
        return normalizeAttributeCriterion(criterion.attribute, false);
      }
      if (criterion?.segment) {
        return normalizeSegmentCriterion(criterion.segment, false);
      }
      return null;
    })
    .filter((expr): expr is NormalizedExpression.Expression => expr !== null);
};

const normalizeTargetingExpression = (
  expression: TargetingExpression,
  isNegated: boolean = false,
  criteria?: { [key: string]: Criterion },
): NormalizedExpression.Expression | null => {
  // Handle reference to a criterion
  if (expression.ref) {
    const criterion = criteria?.[expression.ref];
    if (criterion?.attribute) {
      return normalizeAttributeCriterion(criterion.attribute, isNegated);
    }
    if (criterion?.segment) {
      return normalizeSegmentCriterion(criterion.segment, isNegated);
    }
    return null;
  }

  // Handle NOT
  if (expression.not) {
    // Handle NOT with a ref
    if (expression.not.ref) {
      const criterion = criteria?.[expression.not.ref];
      if (criterion?.attribute) {
        return normalizeAttributeCriterion(criterion.attribute, !isNegated);
      }
      if (criterion?.segment) {
        return normalizeSegmentCriterion(criterion.segment, !isNegated);
      }
      return null;
    }

    // Handle NOT with nested expression (recursively)
    return normalizeTargetingExpression(expression.not, !isNegated, criteria);
  }

  // Handle AND
  if (expression.and?.operands) {
    const operands = expression.and.operands
      .map(operand => {
        // Handle nested expressions recursively
        if (operand.ref) {
          const criterion = criteria?.[operand.ref];
          if (criterion?.attribute) {
            return normalizeAttributeCriterion(criterion.attribute, isNegated);
          }
          if (criterion?.segment) {
            return normalizeSegmentCriterion(criterion.segment, isNegated);
          }
          return null;
        }

        // Handle nested NOT expression
        if (operand.not) {
          return normalizeTargetingExpression(
            { not: operand.not },
            isNegated,
            criteria,
          );
        }

        // Handle nested AND expression
        if (operand.and) {
          return normalizeTargetingExpression(
            { and: operand.and },
            isNegated,
            criteria,
          );
        }

        // Handle nested OR expression
        if (operand.or) {
          return normalizeTargetingExpression(
            { or: operand.or },
            isNegated,
            criteria,
          );
        }

        return null;
      })
      .filter((expr): expr is NormalizedExpression.Expression => expr !== null);

    if (operands.length === 0) {
      return null;
    }

    if (operands.length === 1) {
      return operands[0];
    }

    return {
      type: 'logical',
      operator: isNegated ? 'or' : 'and',
      operands,
    };
  }

  // Handle OR
  if (expression.or?.operands) {
    const operands = expression.or.operands
      .map(operand => {
        // Handle nested expressions recursively
        if (operand.ref) {
          const criterion = criteria?.[operand.ref];
          if (criterion?.attribute) {
            return normalizeAttributeCriterion(criterion.attribute, isNegated);
          }
          if (criterion?.segment) {
            return normalizeSegmentCriterion(criterion.segment, isNegated);
          }
          return null;
        }

        // Handle nested NOT expression
        if (operand.not) {
          return normalizeTargetingExpression(
            { not: operand.not },
            isNegated,
            criteria,
          );
        }

        // Handle nested AND expression
        if (operand.and) {
          return normalizeTargetingExpression(
            { and: operand.and },
            isNegated,
            criteria,
          );
        }

        // Handle nested OR expression
        if (operand.or) {
          return normalizeTargetingExpression(
            { or: operand.or },
            isNegated,
            criteria,
          );
        }

        return null;
      })
      .filter((expr): expr is NormalizedExpression.Expression => expr !== null);

    if (operands.length === 0) {
      return null;
    }

    if (operands.length === 1) {
      return operands[0];
    }

    return {
      type: 'logical',
      operator: isNegated ? 'and' : 'or',
      operands,
    };
  }

  return null;
};

export const normalizeTargeting = (
  targeting: SegmentTargeting | null | undefined,
): NormalizedExpression.Expression | null => {
  if (!targeting) {
    return null;
  }

  // Normalize expression if present (this respects the logical structure)
  if (targeting.expression) {
    return normalizeTargetingExpression(
      targeting.expression,
      false,
      targeting.criteria,
    );
  }

  // If no expression but we have criteria, normalize all criteria as an AND group
  if (targeting.criteria) {
    const expressions = normalizeCriteria(targeting.criteria);
    if (expressions.length === 0) {
      return null;
    }
    if (expressions.length === 1) {
      return expressions[0];
    }
    return {
      type: 'logical',
      operator: 'and',
      operands: expressions,
    };
  }

  return null;
};
